import { useState, useMemo, useEffect } from "react";
import "@fontsource/josefin-sans";
import { BrowserRouter as Router, Routes, Route } from "react-router";
import { ThemeProvider, CssBaseline, Box, AppBar, Toolbar } from "@mui/material";
import { Close } from "@mui/icons-material";
import { enqueueSnackbar, closeSnackbar, SnackbarProvider } from "notistack";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { onAuthStateChanged } from "firebase/auth";
import thanq from "./api/thanq";
import useAdminStore from "./store/adminStore";
import Dashboard from "./pages/Dashboard";
import Main from "./pages/Main";
import Users from "./pages/Users";
import Tips from "./pages/Tips";
import Login from "./pages/Login";
import Config from "./pages/Config";
import Companies from "./pages/Companies";
import Unauthorized from "./components/Unauthorized";

import Sidebar, { SidebarToggle } from "./components/Sidebar";
import { Loading } from "./components/Loading";

import { auth } from "./utils/firebase";
import getTheme from "./theme";

// Route wrapper that checks admin role
const ProtectedRoute = ({ children }) => {
    const role = useAdminStore((s) => s.role);
    return role === "admin" ? <Unauthorized /> : children;
};

// Route configuration
const routes = [
    { path: "/", component: Main, protected: false },
    { path: "/dashboard", component: Dashboard, protected: true },
    { path: "/users", component: Users, protected: false },
    { path: "/tips", component: Tips, protected: true },
    { path: "/config", component: Config, protected: true },
    { path: "/companies", component: Companies, protected: true },
];

function App() {
    const [darkMode, setDarkMode] = useState(true);
    const [loggedIn, setLoggedIn] = useState(false);
    const [authLoading, setAuthLoading] = useState(true); // Add loading state
    const [mobileOpen, setMobileOpen] = useState(false);
    const setRole = useAdminStore((s) => s.setRole);
    const setCompany = useAdminStore((s) => s.setCompany);
    const clear = useAdminStore((s) => s.clear);
    useEffect(() => {
        // Add all icons to the library
        library.add(fas);

        // Listen to Firebase auth state changes instead of localStorage
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setLoggedIn(true);
                // Fetch role when authenticated
                thanq
                    .get("/admin/company")
                    .then(({ data }) => {
                        const role = data?.role || null;
                        const company = data?.company || null;
                        if (role && company) {
                            setRole(role);
                            setCompany(company);
                        }
                    })
                    .catch(() => {
                        clear();
                    });
            } else {
                setLoggedIn(false);
                clear();
            }
            setAuthLoading(false);
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, [setRole, setCompany, clear]);

    const theme = useMemo(() => getTheme(darkMode ? "dark" : "light"), [darkMode]);

    const handleLogout = async () => {
        try {
            await auth.signOut();
            // No need to manually update state - onAuthStateChanged will handle it
        } catch (error) {
            enqueueSnackbar(error.message, { variant: "error" });
        }
    };

    const handleToggleDarkMode = () => setDarkMode((prev) => !prev);
    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    // Show loading while checking auth state
    if (authLoading) {
        return (
            <ThemeProvider theme={theme}>
                <Loading />
            </ThemeProvider>
        );
    }

    if (!loggedIn) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Login />
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <SnackbarProvider
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                preventDuplicate={true}
                autoHideDuration={5000}
                maxSnack={3}
                action={(key) => (
                    <Close
                        onClick={() => {
                            closeSnackbar(key);
                        }}
                    />
                )}
            >
                <CssBaseline />
                <Router>
                    <Box sx={{ display: "flex" }}>
                        <Sidebar
                            darkMode={darkMode}
                            onToggleDarkMode={handleToggleDarkMode}
                            onLogout={handleLogout}
                            mobileOpen={mobileOpen}
                            onMobileClose={handleDrawerToggle}
                        />
                        <Box
                            component="main"
                            sx={{
                                flexGrow: 1,
                                ml: { xs: 0, md: "240px" },
                                minHeight: "100vh",
                                bgcolor: "background.default",
                                p: 3,
                                width: { xs: "100%", md: "calc(100% - 240px)" },
                            }}
                        >
                            <AppBar
                                position="fixed"
                                sx={{
                                    width: { xs: "100%", md: "calc(100% - 240px)" },
                                    ml: { xs: 0, md: "240px" },
                                    display: { xs: "block", md: "none" },
                                }}
                            >
                                <Toolbar>
                                    <SidebarToggle onMobileOpen={handleDrawerToggle} />
                                    
                                </Toolbar>
                            </AppBar>
                            <Toolbar sx={{ display: { xs: "block", md: "none" } }} />
                            <Routes>
                                {routes.map(
                                    ({ path, component: Component, protected: isProtected }) => (
                                        <Route
                                            key={path}
                                            path={path}
                                            element={
                                                isProtected ? (
                                                    <ProtectedRoute>
                                                        <Component />
                                                    </ProtectedRoute>
                                                ) : (
                                                    <Component />
                                                )
                                            }
                                        />
                                    )
                                )}
                            </Routes>
                        </Box>
                    </Box>
                </Router>
            </SnackbarProvider>
        </ThemeProvider>
    );
}

export default App;
